import { usePermissionData } from "AppContext"
import Breadcrumb from "components/Common/Breadcrumb"
import Loader from "components/Loader/Loader"
import PermittedComponent from "PermittedComponent"
import React, { useEffect, useState } from "react"
import { FaFilePdf } from "react-icons/fa"
import { MdSave } from "react-icons/md"
import {
  Card,
  CardBody,
  Table,
  Row,
  Col,
  Button,
  Input,
  Label,
} from "reactstrap"
import Swal from "sweetalert2"
import { fetchData, FetchPostData } from "utils/Common_api_functions"

const SalarySheet = () => {
  const currentMonth = new Date().getMonth() + 1 // 1–12
  const currentYear = new Date().getFullYear()

  const [loading, setLoading] = useState(false)
  const [month, setMonth] = useState("")
  const [year, setYear] = useState("")
  const [salaryData, setSalaryData] = useState([])

  useEffect(() => {
    if (month && year) {
      handleGenerate()
    }
  }, [month, year])

  const handleGenerate = async () => {
    const APIdata = await FetchPostData(setLoading, "salary", {
      month,
      year,
    })
    if (APIdata?.data?.success) {
      setSalaryData(APIdata?.data?.data)
    } else {
      Swal.fire({
        title: "Error Generating Salary Sheet",
        text: APIdata?.response?.data?.message,
        icon: "error",
      })
    }
  }

  const handleSave = async () => {
    const APIdata = await FetchPostData(setLoading, "salary/process", {
      month,
      year,
      salaryData,
    })
    if (APIdata?.data?.success) {
      Swal.fire({
        title: "Salary Processed Successfully.",
        text: APIdata?.data?.message,
        icon: "success",
      })
    } else {
      Swal.fire({
        title: "Error Generating Salary Sheet",
        text: APIdata?.response?.data?.message,
        icon: "error",
      })
    }
  }

  // 🔢 Total calculation
  const totals = salaryData.reduce(
    (acc, item) => {
      acc.totalWorkingDays += item.totalWorkingDays
      acc.totalSalary += item.totalSalary
      acc.totalDeduction += item.totalDeduction
      acc.totalExpense += item?.totalExpense || 0
      acc.finalSalary += item.finalSalary
      acc.totaladvance += item?.totalAdvance
      return acc
    },
    {
      totalWorkingDays: 0,
      totalSalary: 0,
      totalDeduction: 0,
      totalExpense: 0,
      finalSalary: 0,
      totaladvance: 0,
    }
  )

  const { checkAccess } = usePermissionData()

  return (
    <PermittedComponent isAccess={checkAccess(5, "view")}>
      <div className="page-content">
        {loading && <Loader />}
        <Breadcrumb title="Payroll" breadcrumbItem="Salary Sheet" />

        <Card>
          <CardBody>
            {/* 🔹 Filters */}
            <Row className="align-items-end mb-4">
              <Col md={2}>
                <Label>Month</Label>
                <Input
                  type="select"
                  className="rounded-4"
                  value={month}
                  onChange={e => setMonth(e.target.value)}
                >
                  <option value="" disabled>
                    Select Month
                  </option>

                  {[
                    "January",
                    "February",
                    "March",
                    "April",
                    "May",
                    "June",
                    "July",
                    "August",
                    "September",
                    "October",
                    "November",
                    "December",
                  ].map((name, index) => (
                    <option key={index + 1} value={index + 1}>
                      {name}
                    </option>
                  ))}
                </Input>
              </Col>

              <Col md={2}>
                <Label>Year</Label>
                <Input
                  type="select"
                  className="rounded-4"
                  value={year}
                  onChange={e => setYear(e.target.value)}
                >
                  <option value="" disabled>
                    Select Year
                  </option>

                  {Array.from(
                    { length: new Date().getFullYear() - 2025 + 1 },
                    (_, i) => 2025 + i
                  ).map(y => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </Input>
              </Col>

              {checkAccess(5, "edit") && (
                <Col md={8} className="text-end">
                  {salaryData.length > 0 && (
                    <Button className="btn-blue rounded-4" onClick={handleSave}>
                      <MdSave /> Generate Salary Sheet
                    </Button>
                  )}
                </Col>
              )}
            </Row>

            {/* 🔹 Salary Table */}
            {salaryData.length > 0 && (
              <Table bordered responsive>
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>Employee</th>
                    <th>Working Days</th>
                    <th>Present</th>
                    <th>Absent</th>
                    <th>Holiday</th>
                    <th>CL</th>
                    <th>SL</th>
                    <th>Paid Off</th>
                    <th>TA (₹)</th>
                    <th>DA (₹)</th>
                    <th>Other Expense (₹)</th>
                    <th>Basic Salary</th>
                    <th>Total Advance</th>
                    <th>Total Deduction</th>
                    <th>Final Salary</th>
                  </tr>
                </thead>

                <tbody>
                  {/* 🔹 TOTAL ROW */}
                  <tr className="table-secondary fw-bold">
                    <td colSpan="12" className="text-end pe-5">
                      TOTAL
                    </td>
                    <td>₹ {totals.totalSalary}</td>
                    <td>₹ {totals.totaladvance}</td>
                    <td>₹ {Number(totals.totalDeduction).toFixed(2)}</td>
                    <td>₹ {Number(totals.finalSalary).toFixed(2)}</td>
                  </tr>
                  {salaryData.map((row, index) => (
                    <tr key={row.id}>
                      <td>{index + 1}</td>
                      <td>{row.employee}</td>
                      <td>{row.workingDays}</td>
                      <td>{row.present}</td>
                      <td>{row.absent}</td>
                      <td>{row.holiday}</td>
                      <td>{row.CL}</td>
                      <td>{row.SL}</td>
                      <td>{row.paidOff}</td>

                      <td>{row.detailedExpense?.travel || 0}</td>
                      <td>{row.detailedExpense?.dearness || 0}</td>
                      <td>{row.detailedExpense?.other || 0}</td>
                      <td>₹ {row.totalSalary}</td>
                      <td>₹ {row.totalAdvance}</td>
                      <td>₹ {Number(row.totalDeduction).toFixed(2)}</td>
                      <td>
                        <strong>₹ {Number(row.finalSalary)?.toFixed(2)}</strong>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </CardBody>
        </Card>
      </div>
    </PermittedComponent>
  )
}

export default SalarySheet
